import { Fragment } from '@wordpress/element';
import { InspectorControls, useBlockProps } from '@wordpress/block-editor';
import { PanelBody, SelectControl } from '@wordpress/components';
import { createHigherOrderComponent } from '@wordpress/compose';
import { addFilter, applyFilters } from '@wordpress/hooks';
import { registerBlockType, getBlockType } from '@wordpress/blocks';
import classnames from 'classnames';

// Extend BlockEdit component to include additional controls for core/image and core/column
const withAnimationControls = createHigherOrderComponent((BlockEdit) => {
    return (props) => {
        const { name, attributes, setAttributes } = props;
        const { animationType = 'none' } = attributes; // Ensuring default if undefined

        // Apply only to image and column blocks
        if (!['core/image', 'core/column'].includes(name)) {
            return <BlockEdit {...props} />;
        }

        return (
            <Fragment>
                <BlockEdit {...props} />
                <InspectorControls>
                    <PanelBody title="🟩 Animation on Scroll" initialOpen={true}>
                        <SelectControl
                            label="Animation Style"
                            value={animationType}
                            options={[
                                { label: 'None', value: 'none' },
                                { label: 'Reveal', value: 'reveal' }
                            ]}
                            onChange={(newAnimationType) => setAttributes({ animationType: newAnimationType })}
                        />
                    </PanelBody>
                </InspectorControls>
            </Fragment>
        );
    };
}, 'withAnimationControls');

addFilter('editor.BlockEdit', 'custom/with-animation-controls', withAnimationControls);

// Filter function to add animationType attribute and modify save function for core/image and core/column
const addAnimationAttributes = (settings, name) => {
    if (!['core/image', 'core/column'].includes(name)) {
        return settings;
    }

    const originalSave = settings.save;

    // Extend settings to include the new attribute and a customized save function
    return {
        ...settings,
        attributes: {
            ...settings.attributes,
            animationType: {
                type: 'string',
                default: 'none'
            }
        },
        save: (props) => {
            const { animationType = 'none' } = props.attributes;
            const blockProps = useBlockProps.save({
                className: classnames(props.attributes.className, {
                    'hc-reveal': animationType === 'reveal'
                })
            });

            // Return the original save function with enhanced properties
            return originalSave({ ...props, attributes: { ...props.attributes, className: blockProps.className } });
        },
    };
};

addFilter('blocks.registerBlockType', 'custom/add-animation-attributes', addAnimationAttributes);

// Apply additional classes based on animationType attribute
const applyExtraClass = (extraProps, blockType, attributes) => {
    const { animationType = 'none' } = attributes;
    if (['core/image', 'core/column'].includes(blockType.name)) {
        extraProps.className = classnames(extraProps.className, {
            'hc-reveal': animationType === 'reveal'
        });
    }
    return extraProps;
};

addFilter('blocks.getSaveContent.extraProps', 'custom/apply-extra-class', applyExtraClass);
